// src/components/analytics/ProductionTrendChart.js
import React from 'react';
import { Box, Paper, Typography, Card, CardContent } from '@mui/material';
import { TrendingUp, ShowChart } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ProductionTrendChart = ({ data, selectedYear, showDetails = false, t }) => {
  // Підготовка даних для графіка
  const chartData = data.map((monthData, index) => ({
    name: t(`month_${index}`),
    tunnel: Math.round(monthData.tunnel),
    washing: Math.round(monthData.washing),
    total: Math.round(monthData.total),
    month: index + 1
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card variant="outlined">
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {label}
            </Typography>
            {payload.map((entry, index) => (
              <Typography 
                key={index} 
                variant="body2" 
                style={{ color: entry.color }}
              >
                {entry.name}: {Math.round(entry.value).toLocaleString()} {t('analytics_units_kg')}
              </Typography>
            ))}
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, height: showDetails ? 500 : 400 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ShowChart color="primary" />
        <Typography variant="h6">
          {t('analytics_production_trends')} {selectedYear}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={showDetails ? 400 : 300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="tunnel" 
            stroke="#8884d8" 
            strokeWidth={3}
            name={t('analytics_tunnel_production')}
            dot={{ r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="washing" 
            stroke="#82ca9d" 
            strokeWidth={3}
            name={t('analytics_washing_production')}
            dot={{ r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="total" 
            stroke="#ffc658" 
            strokeWidth={3}
            name={t('analytics_total_production')}
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {showDetails && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('analytics_trend_analysis')}: {getTrendAnalysis(data, t)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

// Допоміжна функція для аналізу трендів
const getTrendAnalysis = (data, t) => {
  const recentData = data.slice(-3).filter(d => d.total > 0);
  const previousData = data.slice(-6, -3).filter(d => d.total > 0);
  
  if (recentData.length < 2 || previousData.length < 2) {
    return t('analytics_insufficient_data');
  }

  const recentAvg = recentData.reduce((sum, d) => sum + d.total, 0) / recentData.length;
  const previousAvg = previousData.reduce((sum, d) => sum + d.total, 0) / previousData.length;
  
  const change = ((recentAvg - previousAvg) / previousAvg) * 100;
  
  if (change > 10) return t('analytics_strong_growth');
  if (change > 5) return t('analytics_moderate_growth');
  if (change > -5) return t('analytics_stable');
  if (change > -10) return t('analytics_moderate_decline');
  return t('analytics_significant_decline');
};

export default ProductionTrendChart;